import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Collapse, useTheme } from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { NavItem, SidebarProps } from './Sidebar.types';
import { NAV_ITEMS } from './Sidebar.constants';
import {
  StyledDrawer,
  StyledList,
  StyledListItem,
  StyledListItemButton,
  StyledListItemText,
  ExpandIcon,
  NestedList,
  SidebarContainer,
  ItemContainer,
} from './Sidebar.styles';

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  width = 320,
  navItems = NAV_ITEMS,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Filter out invalid nav items (empty titles)
  const validNavItems = useMemo(() => {
    if (!navItems || navItems.length === 0) {
      console.warn('No nav items provided to Sidebar, using default NAV_ITEMS');
      return NAV_ITEMS;
    }
    
    // Check if all nav items have empty titles
    const allEmpty = navItems.every(item => !item.title || item.title.trim() === '');
    if (allEmpty) {
      console.warn('All nav items have empty titles, using default NAV_ITEMS');
      return NAV_ITEMS;
    }
    
    // Filter out items with empty titles
    const filtered = navItems.filter(item => item.title && item.title.trim() !== '');
    if (filtered.length === 0) {
      console.warn('After filtering empty titles, no nav items remain, using default NAV_ITEMS');
      return NAV_ITEMS;
    }
    
    return filtered;
  }, [navItems]);

  // Helper function to check if an item or any of its children is selected
  const isItemOrChildSelected = (item: NavItem): boolean => {
    if (item.path === location.pathname) {
      return true;
    }
    if (item.children && item.children.length > 0) {
      return item.children.some(child => isItemOrChildSelected(child));
    }
    return false;
  };

  // Find all parents that should be expanded based on the current path
  const findAllParentsToExpand = (items: NavItem[]): string[] => {
    const parentsToExpand: string[] = [];
    
    const checkItem = (item: NavItem) => {
      if (item.children && item.children.length > 0) {
        // Check if this item has a selected child
        const hasSelectedChild = item.children.some(child => isItemOrChildSelected(child));
        
        // If it has a selected child, add this item to the expandedItems
        if (hasSelectedChild) {
          parentsToExpand.push(item.title);
        }
        
        // Recursively check all children
        item.children.forEach(checkItem);
      }
    };
    
    items.forEach(checkItem);
    return parentsToExpand;
  };

  // Update expanded items whenever the location changes
  useEffect(() => {
    const parentsToExpand = findAllParentsToExpand(validNavItems);
    
    setExpandedItems(prev => {
      const newExpanded = [...prev];
      
      // Add all parents that should be expanded
      parentsToExpand.forEach(title => {
        if (!newExpanded.includes(title)) {
          newExpanded.push(title);
        }
      });
      
      return newExpanded;
    });
  }, [location.pathname, validNavItems]);

  const handleItemClick = (item: NavItem) => {
    if (item.children) {
      setExpandedItems(prev =>
        prev.includes(item.title)
          ? prev.filter(i => i !== item.title)
          : [...prev, item.title]
      );
    } else if (item.path) {
      navigate(item.path);
      if (window.innerWidth < 600) {
        onToggle();
      }
    }
  };

  // Helper function to check if a parent item has a direct child with the same path
  const hasChildWithSamePath = (item: NavItem): boolean => {
    if (!item.children || item.children.length === 0 || !item.path) {
      return false;
    }
    
    return item.children.some(child => child.path === item.path);
  };

  // Helper function to check if this item is a child with the same path as its parent
  const isChildWithSamePathAsParent = (item: NavItem, parentItems: NavItem[]): boolean => {
    if (!item.path) return false;
    
    return parentItems.some(parent => 
      parent.path === item.path && 
      parent.children?.some(child => child.path === item.path)
    );
  };

  const renderNavItems = (items: NavItem[], level = 0, parentItems: NavItem[] = []) => {
    if (!items) return null;
    return items.map((item, index) => {
      const isDirectlySelected = item.path === location.pathname;
      
      // Check if any child is selected (will always return a boolean)
      const hasSelectedChild = Boolean(
        item.children && item.children.some(child => isItemOrChildSelected(child))
      );
      
      const isExpanded = expandedItems.includes(item.title);
      const hasChildren = item.children && item.children.length > 0;
      
      // Check for parent-child with same path special case
      const hasSamePathChild = hasChildWithSamePath(item);
      
      // Parent item selection logic:
      let parentSelectionState = false;
      
      if (isExpanded) {
        // When expanded, a parent should NOT show as selected if:
        // 1. It has the same URL as one of its nested items, OR
        // 2. Any of its nested items is selected
        parentSelectionState = isDirectlySelected && !hasSamePathChild && !hasSelectedChild;
      } else {
        // When collapsed, a parent should show as selected if:
        // 1. It is directly selected, OR
        // 2. It has any selected child (including if child has same URL)
        parentSelectionState = isDirectlySelected || hasSelectedChild;
      }
      
      // Child item selection logic
      // Show as selected when it is directly selected
      const childSelectionState = level > 0 && isDirectlySelected;
      
      // Final selection state combines both cases
      const finalSelectionState = level === 0 ? parentSelectionState : childSelectionState;
      
      // Create a unique key using title, path, and index
      const uniqueKey = `${item.title}-${item.path || ''}-${level}-${index}`;

      // Update the parent items list for the next level of recursion
      const nextLevelParentItems = [...parentItems];
      if (hasChildren) {
        nextLevelParentItems.push(item);
      }

      return (
        <ItemContainer 
          key={uniqueKey} 
          isExpanded={isExpanded}
          isSelected={finalSelectionState}
          data-testid={`sidebar-item-container-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <StyledListItem
            disablePadding
            level={level}
            isExpanded={isExpanded}
            isSelected={finalSelectionState}
            data-testid={`sidebar-list-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <StyledListItemButton
              selected={finalSelectionState}
              onClick={() => handleItemClick(item)}
              level={level}
              data-testid={`sidebar-button-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <StyledListItemText
                primary={item.title}
                level={level}
                isExpanded={isExpanded}
                isSelected={finalSelectionState}
                data-testid={`sidebar-text-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              />
              {hasChildren && (
                <ExpandIcon 
                  isExpanded={isExpanded}
                  data-testid={`sidebar-expand-icon-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ExpandIcon>
              )}
            </StyledListItemButton>
          </StyledListItem>
          {hasChildren && item.children && (
            <Collapse 
              in={isExpanded} 
              timeout="auto" 
              unmountOnExit
              data-testid={`sidebar-collapse-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <NestedList 
                disablePadding 
                isExpanded={isExpanded}
                data-testid={`sidebar-nested-list-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {renderNavItems(item.children, level + 1, nextLevelParentItems)}
              </NestedList>
            </Collapse>
          )}
        </ItemContainer>
      );
    });
  };

  return (
    <StyledDrawer
      variant="persistent"
      anchor="left"
      open={isOpen}
      width={width}
      data-testid="sidebar-drawer"
    >
      <SidebarContainer data-testid="sidebar-container">
        <StyledList data-testid="sidebar-list">
          {validNavItems && validNavItems.length > 0 ? (
            renderNavItems(validNavItems)
          ) : (
            <ItemContainer key="no-items" isExpanded={false}>
              <StyledListItem disablePadding level={0} isExpanded={false}>
                <StyledListItemButton level={0}>
                  <StyledListItemText 
                    primary="No navigation items available" 
                    level={0}
                  />
                </StyledListItemButton>
              </StyledListItem>
            </ItemContainer>
          )}
        </StyledList>
      </SidebarContainer>
    </StyledDrawer>
  );
};

export default Sidebar; 